import { useT } from '@/lib/i18n/useT';
import type { AssistantContextKind } from './useAIContext';

interface SuggestedChipsProps {
  chips: string[];
  onPick: (text: string) => void;
  disabled?: boolean;
  /**
   * When the AI hasn't yet emitted any chips (cold start, pre-kickoff),
   * show context-aware starters so the manager has something useful to
   * click instead of waiting for the model. The AI's own chips take over
   * once they arrive — no flicker because `chips` is a different array.
   */
  contextKind?: AssistantContextKind;
}

const STARTER_CHIPS: Partial<Record<AssistantContextKind, string[]>> = {
  // Per the plan: chips tailored for the Station ("session") context, taking
  // advantage of the enriched PAGE_DATA (inventory.lowStock, responses
  // trend/topAnswers) the backend now ships.
  session: [
    '¿Qué prize se está agotando primero?',
    '¿Qué tendencia tienen las respuestas en las últimas 24h?',
    '¿Cuál es la respuesta más común al primer componente?',
    'Compara esta station con su evento padre',
  ],
};

export function SuggestedChips({
  chips,
  onPick,
  disabled,
  contextKind,
}: SuggestedChipsProps) {
  const t = useT();
  const effective =
    chips.length > 0
      ? chips
      : (contextKind && STARTER_CHIPS[contextKind]) ?? [];
  if (effective.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 border-t border-[var(--color-xk-border)] bg-[var(--color-xk-bg-nav)] px-3 py-2">
      <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--color-xk-text-subtle)]">
        {t('assistant.suggestions')}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {effective.map((chip, i) => (
          <button
            key={`${i}-${chip}`}
            type="button"
            disabled={disabled}
            onClick={() => onPick(chip)}
            className="rounded-full border border-[var(--color-accent-20)] bg-[var(--color-accent-08)] px-3 py-1 text-[11px] text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-accent-15)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  );
}
